import { type Event, EventTypeEnum, ParticipantTypeEnum } from '@clubspark-node/clubspark-tennis-types';

import { type LevelConfiguration } from 'atoms/tournament';
import {
  type ScaleAttributes,
  ScaleTypeEnum,
} from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/types';
import { type ScaleItem } from 'typedefs/store/tmxTypes';
import { sum } from 'utils/math';

import { type ParticipantScaling, type ScalingPurpose } from '.';
import Scaling from './Scaling';

const scaleType = ScaleTypeEnum.RANKING;
const sortDirection = 'inverted';
const emptyValue = 0;

// Note: Not sure we need to check this extension if we're checking participantType and TEAM events already?
// const isTeamType = !!event?.extensions?.find((ext) => ext.name === 'division')?.value?.familyType
export const isCustomEvent = (ratingCategory) => {
  return !!(ratingCategory?.minimumValue && ratingCategory?.maximumValue);
};

export const getCustomLevel = (ratingCategory) => {
  if (ratingCategory?.minimumValue && ratingCategory?.maximumValue) {
    const rating = `${ratingCategory.ratingType}${ratingCategory.minimumValue}-${ratingCategory.maximumValue}`;
    return rating.replace('.', '_');
  }
  return null;
};

export interface RankingScaleItem extends ScaleItem {
  scaleValue: number;
}

export const getRankingValue = (scaleItem: RankingScaleItem): number => scaleItem?.scaleValue ?? Infinity;

class RankingScaling extends Scaling {
  static scaleType = scaleType;
  static sortDirection: 'normal' | 'inverted' = sortDirection;
  static defaultScaleValue = emptyValue;

  public static scaleSortMethod = (
    purpose: ScalingPurpose,
    a: ParticipantScaling<RankingScaleItem>,
    b: ParticipantScaling<RankingScaleItem>,
  ): number =>
    this.hasValueCompare(b) - this.hasValueCompare(a) ||
    this.getValue(a) - this.getValue(b) ||
    this.getBestValue(a) - this.getBestValue(b);

  public static getDisplay(participantScaling: ParticipantScaling<RankingScaleItem>): string {
    return this.getText(participantScaling);
  }

  public static getText(participantScaling: ParticipantScaling<RankingScaleItem>): string {
    const values =
      participantScaling.participant?.participantType === ParticipantTypeEnum.PAIR
        ? this.getIndividualParticipantScalings.call(this, participantScaling).map(this.getValue.bind(this))
        : [this.getValue.call(this, participantScaling)];
    const value = !values.some((v) => Number.isFinite(v) && v > 0) ? emptyValue : sum(values);

    return `${Number.isFinite(value) && value > 0 ? Math.round(value * 10) / 10 : '-'}${
      participantScaling.participant?.participantType === ParticipantTypeEnum.PAIR
        ? ` (${this.getIndividualParticipantScalings
            .call(this, participantScaling)
            .map(this.getText.bind(this))
            .join('/')})`
        : ''
    }`;
  }

  public static getValue({ participant, scaleItems }: ParticipantScaling<RankingScaleItem>): number {
    const values = scaleItems.map(getRankingValue).filter((value) => Number.isFinite(value) && value > 0);
    const value = values.reduce((total, rating) => total + rating, 0) || Infinity;
    return participant?.participantType === ParticipantTypeEnum.PAIR ? value : value / values.length;
  }

  public static hasValueCompare = (participantScaling) => {
    const values =
      participantScaling.participant?.participantType === ParticipantTypeEnum.PAIR
        ? this.getIndividualParticipantScalings.call(this, participantScaling).map(this.getValue.bind(this))
        : [this.getValue(participantScaling)];
    return values.reduce((total, next) => total + (Number.isFinite(next) ? 1 : -1), 0);
  };

  public static getScaleAttributes(scaleNameFn: (scaleName: string) => string, event: Event): ScaleAttributes {
    const categoryName = event?.category?.categoryName || event?.category?.ageCategoryCode || event?.eventId;
    const ratingCategory = event?.extensions?.find((e) => e.name === 'division')?.value?.ratingCategory;
    return {
      scaleName: scaleNameFn(`${categoryName}${isCustomEvent(ratingCategory) ? getCustomLevel(ratingCategory) : ''}`),
      scaleType,
      eventType: event?.eventType === EventTypeEnum.TEAM ? EventTypeEnum.SINGLES : event?.eventType,
    };
  }

  public static getIndividualParticipantScalings = (
    participantScaling: ParticipantScaling<RankingScaleItem>,
  ): ParticipantScaling<RankingScaleItem>[] =>
    participantScaling.scaleItems.map((scaleItem) => ({
      ...participantScaling,
      participant: { ...participantScaling.participant, participantType: ParticipantTypeEnum.INDIVIDUAL },
      scaleItems: [scaleItem],
    }));
  public static getBestValue = ({ scaleItems }: ParticipantScaling<RankingScaleItem>): number => {
    // Using mean average
    const values = scaleItems.map(getRankingValue);
    return Math.min.apply(null, values);
  };
  static getIsAvailable(_, levelConfig?: Partial<LevelConfiguration>) {
    return levelConfig?.rankingsEnabled;
  }

  public static getDisplayName = (): string => 'Ranking';
}

export default RankingScaling;
